import React, { useState, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcFileInvoiceDollar, IcArrowDown, IcCheckCircle, IcWallet, IcDollar, IcEdit, IcBan, IcExclamationTri } from "../../Icons";
import axios from "axios";
import {
  Table,
  Button,
  Row,
  Col,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Badge,
  FormGroup,
} from "reactstrap";
import Header from "../../layout/Header/Header";
import Swal from "sweetalert2";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import moment from "moment";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n || 0);

function ListadoAnticiposClientes() {
  const { user } = useContext(AuthContext);
  const URL_ANTICIPOS = process.env.REACT_APP_URL_ANTICIPOS_CLIENTES;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const URL_CARGOS = process.env.REACT_APP_URL_CARGOS;
  const URL_ABONOS = process.env.REACT_APP_URL_ABONOS;
  // Endpoint especial: crea abono sin actualizar cliente (ya lo hizo el anticipo)
  const URL_ABONOS_ANTICIPO = URL_ABONOS.replace(/abonos$/, "abonosAplicarAnticipo");
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 60);
  const endDate = moment(hoy).format("YYYY-MM-DD");
  const startDate = moment(inicio).format("YYYY-MM-DD");

  /* ── filters ── */
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);

  /* ── data ── */
  const [comments, setComments] = useState([]);
  const [clientes, setClientes] = useState([]);

  /* ── pagination / search ── */
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  /* ── modal nuevo anticipo ── */
  const [modalCreate, setModalCreate] = useState(false);
  const toggleCreate = () => setModalCreate(!modalCreate);
  const [nFecha, setNFecha] = useState(endDate);
  const [nImporte, setNImporte] = useState("");
  const [nCliente, setNCliente] = useState("");
  const [nFormaPago, setNFormaPago] = useState("Transfer");
  const [nObservaciones, setNObservaciones] = useState("");
  const [saving, setSaving] = useState(false);

  /* ── modal editar anticipo ── */
  const [modalEdit, setModalEdit] = useState(false);
  const toggleEdit = () => setModalEdit(!modalEdit);
  const [eId, setEId] = useState("");
  const [eNumero, setENumero] = useState("");
  const [eIdCliente, setEIdCliente] = useState("");
  const [eClienteNombre, setEClienteNombre] = useState("");
  const [eImporte, setEImporte] = useState("");
  const [eImporteViejo, setEImporteViejo] = useState(0);
  const [eUtilizado, setEUtilizado] = useState(0);
  const [eFormaPago, setEFormaPago] = useState("");
  const [eObservaciones, setEObservaciones] = useState("");
  const [eSaving, setESaving] = useState(false);

  /* ── modal aplicar anticipo ── */
  const [modalAplicar, setModalAplicar] = useState(false);
  const toggleAplicar = () => setModalAplicar(!modalAplicar);
  const [apId, setApId] = useState("");
  const [apNumero, setApNumero] = useState("");
  const [apClienteNombre, setApClienteNombre] = useState("");
  const [apSaldo, setApSaldo] = useState(0);
  const [apIdCliente, setApIdCliente] = useState("");
  const [cargosDisponibles, setCargosDisponibles] = useState([]);
  const [totalAplicar, setTotalAplicar] = useState(0);
  const [apLoading, setApLoading] = useState(false);
  const [validaBoton, setValidaBoton] = useState(true);

  const authHeader = {
    headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
  };

  /* ── fetch clientes (once) ── */
  useMemo(() => {
    axios
      .get(URL_CLIENTES, authHeader)
      .then((r) => setClientes(r.data))
      .catch(console.log);
  }, []);

  /* ── fetch anticipos (by date) ── */
  useMemo(() => {
    axios
      .get(
        `${URL_ANTICIPOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        authHeader
      )
      .then((r) => {
        const rows = r.data
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
          .map((a) => ({
            _id: a._id,
            idAnticipoCliente: a.idAnticipoCliente,
            fecha: a.fecha,
            cliente: a.clientes[0]?.nombre_comercial || "—",
            idCliente: a.clientes[0]?._id || "",
            importe: a.importe,
            formaPago: a.formaPago,
            utilizado: a.utilizado,
            saldo: a.saldo,
            observaciones: a.observaciones,
            is_active: a.is_active,
          }));
        setComments(rows);
      })
      .catch(console.log);
  }, [selectedFechaInicio, selectedFechaFin, refreshCount]);

  /* ── summary KPIs ── */
  const summaryStats = useMemo(() => {
    const active = comments.filter((c) => c.is_active === "Si");
    const totalImporte = active.reduce((s, c) => s + c.importe, 0);
    const totalUtilizado = active.reduce((s, c) => s + c.utilizado, 0);
    const totalSaldo = active.reduce((s, c) => s + c.saldo, 0);
    const count = active.length;
    return { count, totalImporte, totalUtilizado, totalSaldo };
  }, [comments]);

  /* ── table filter ── */
  const commentsData = useMemo(() => {
    let data = comments;
    if (search)
      data = data.filter((c) =>
        c.cliente.toLowerCase().includes(search.toLowerCase())
      );
    if (selectedCliente)
      data = data.filter((c) => c.idCliente === selectedCliente);
    setTotalItems(data.length);
    if (sorting.field) {
      const rev = sorting.order === "asc" ? 1 : -1;
      data = [...data].sort(
        (a, b) => rev * String(a[sorting.field]).localeCompare(String(b[sorting.field]))
      );
    }
    return data.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting, selectedCliente]);

  /* ── crear nuevo anticipo ── */
  async function saveAnticipo() {
    if (!nFecha || !nImporte || !nCliente) {
      return Swal.fire("Campos incompletos", "Fecha, Importe y Cliente son requeridos", "warning");
    }
    setSaving(true);
    try {
      const imp = parseFloat(nImporte);
      await axios.post(
        URL_ANTICIPOS,
        {
          fecha: nFecha,
          importe: imp,
          saldo: imp,
          utilizado: 0,
          clientes: nCliente,
          formaPago: nFormaPago,
          observaciones: nObservaciones || "NA",
        },
        authHeader
      );
      await axios.post(
        URL_LOGS,
        { tipo: "Crear Anticipo Cliente", detalle: `${nCliente} $${imp}`, user: user.id },
        authHeader
      );
      Swal.fire({ icon: "success", title: "Anticipo creado", timer: 1500, showConfirmButton: false });
      toggleCreate();
      setNFecha(endDate);
      setNImporte("");
      setNCliente("");
      setNFormaPago("Transfer");
      setNObservaciones("");
      setRefreshCount((p) => p + 1);
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Error al crear anticipo", "error");
    } finally {
      setSaving(false);
    }
  }

  /* ── editar anticipo ── */
  function abrirEditar(c) {
    setEId(c._id);
    setENumero(c.idAnticipoCliente);
    setEIdCliente(c.idCliente);
    setEClienteNombre(c.cliente);
    setEImporte(String(c.importe));
    setEImporteViejo(c.importe);
    setEUtilizado(c.utilizado);
    setEFormaPago(c.formaPago || "Transfer");
    setEObservaciones(c.observaciones || "");
    toggleEdit();
  }

  async function saveEditar() {
    const nuevoImporte = parseFloat(eImporte);
    if (!nuevoImporte || nuevoImporte <= 0)
      return Swal.fire("Importe inválido", "El importe debe ser mayor a cero", "warning");
    if (nuevoImporte < eUtilizado)
      return Swal.fire(
        "Importe insuficiente",
        `No puedes reducir el anticipo a ${fmt(nuevoImporte)} porque ya se utilizaron ${fmt(eUtilizado)}`,
        "warning"
      );

    const diff = nuevoImporte - eImporteViejo;

    const confirm = await Swal.fire({
      title: "¿Guardar cambios?",
      text: diff === 0
        ? "Solo se actualizarán forma de pago y observaciones"
        : `El importe cambia ${diff > 0 ? "+" : ""}${fmt(diff)}. Se actualizará el saldo del cliente.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, guardar",
    });
    if (!confirm.isConfirmed) return;

    setESaving(true);
    try {
      // Siempre actualizamos formaPago y observaciones directamente
      // El importe/saldo se actualiza con $inc (diff)
      const payload = {
        formaPago: eFormaPago,
        observaciones: eObservaciones || "NA",
      };

      if (diff !== 0) {
        // El backend usa $inc con la diferencia para actualizar anticipo y cliente
        payload.importe = diff;
        payload.idCliente = eIdCliente;
      }

      await axios.patch(`${URL_ANTICIPOS}/${eId}`, payload, authHeader);

      // Si solo cambiaron forma de pago/observaciones sin cambio de importe,
      // hacemos un patch directo de esos campos (el $inc con diff=0 no afecta nada)
      await axios.post(
        URL_LOGS,
        { tipo: "Editar Anticipo Cliente", detalle: `#${eNumero} ${eClienteNombre}`, user: user.id },
        authHeader
      );

      Swal.fire({ icon: "success", title: "Anticipo actualizado", timer: 1500, showConfirmButton: false });
      toggleEdit();
      setRefreshCount((p) => p + 1);
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Error al editar anticipo", "error");
    } finally {
      setESaving(false);
    }
  }

  /* ── abrir modal aplicar ── */
  function abrirAplicar(c) {
    setApId(c._id);
    setApNumero(c.idAnticipoCliente);
    setApClienteNombre(c.cliente);
    setApSaldo(c.saldo);
    setApIdCliente(c.idCliente);
    setTotalAplicar(0);
    setApLoading(true);
    axios
      .get(`${URL_CARGOS}/cliente/${c.idCliente}`, authHeader)
      .then((r) => {
        const pendientes = r.data
          .filter((cargo) => cargo.saldo > 0)
          .map((cargo) => ({
            id: cargo._id,
            idCargo: cargo.idCargo,
            fecha: cargo.fecha,
            descripcion: cargo.descripcion,
            importe: cargo.importe,
            saldo: cargo.saldo,
            aplicar: 0,
          }));
        setCargosDisponibles(pendientes);
        setApLoading(false);
      })
      .catch(() => setApLoading(false));
    toggleAplicar();
  }

  function handleCambioAplicar(id, value) {
    const updated = cargosDisponibles.map((c) => {
      if (c.id === id) {
        const val = parseFloat(value) || 0;
        if (val > c.saldo) {
          Swal.fire({ icon: "warning", title: "El importe excede el saldo del cargo", timer: 1800, showConfirmButton: false });
          return c;
        }
        return { ...c, aplicar: val };
      }
      return c;
    });
    setCargosDisponibles(updated);
    setTotalAplicar(updated.reduce((s, c) => s + c.aplicar, 0));
  }

  async function saveAplicar() {
    if (totalAplicar <= 0)
      return Swal.fire("Sin importe", "Ingresa al menos un importe a aplicar", "warning");
    if (totalAplicar > apSaldo)
      return Swal.fire("Excede saldo", `El total a aplicar (${fmt(totalAplicar)}) supera el saldo del anticipo (${fmt(apSaldo)})`, "warning");

    const confirm = await Swal.fire({
      title: "¿Aplicar anticipo?",
      text: `Se aplicará ${fmt(totalAplicar)} contra cargos del cliente`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, aplicar",
    });
    if (!confirm.isConfirmed) return;

    setApLoading(true);
    try {
      const toApply = cargosDisponibles.filter((c) => c.aplicar > 0);

      for (const cargo of toApply) {
        // Crear abono SIN actualizar cliente (el anticipo ya lo actualizó)
        await axios.post(
          URL_ABONOS_ANTICIPO,
          {
            fecha: endDate,
            importe: cargo.aplicar,
            clientes: apIdCliente,
            cargos: cargo.id,
            formaPago: "Anticipo",
            comisionPagada: "No",
            observaciones: `Anticipo #${apNumero}`,
            concepto: `Anticipo aplicado al cargo #${cargo.idCargo}`,
          },
          authHeader
        );
        // Actualizar saldo y abonos del cargo
        const nuevoSaldo = cargo.saldo - cargo.aplicar;
        const nuevosAbonos = cargo.importe - nuevoSaldo;
        await axios.patch(
          `${URL_CARGOS}/${cargo.id}`,
          { saldo: nuevoSaldo, abonos: nuevosAbonos },
          authHeader
        );
      }

      // Actualizar anticipo
      await axios.patch(
        `${URL_ANTICIPOS}AplicarAbono/${apId}`,
        { utilizado: totalAplicar, saldo: totalAplicar },
        authHeader
      );

      await axios.post(
        URL_LOGS,
        { tipo: "Aplicar Anticipo Cliente", detalle: `Anticipo #${apNumero} - ${fmt(totalAplicar)}`, user: user.id },
        authHeader
      );

      Swal.fire({ icon: "success", title: "Anticipo aplicado", timer: 1500, showConfirmButton: false });
      toggleAplicar();
      setRefreshCount((p) => p + 1);
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Error al aplicar anticipo", "error");
    } finally {
      setApLoading(false);
    }
  }

  /* ── cancelar anticipo ── */
  function cancelarAnticipo(id, numero) {
    Swal.fire({
      title: "¿Cancelar anticipo?",
      text: `Se revertirá el anticipo #${numero} y se actualizará el saldo del cliente`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axios.get(`${URL_ANTICIPOS}Cancel/${id}`, authHeader);
        Swal.fire({ icon: "success", title: "Cancelado", timer: 1200, showConfirmButton: false });
        setRefreshCount((p) => p + 1);
      } catch (err) {
        Swal.fire("Error", "No se pudo cancelar el anticipo", "error");
      }
    });
  }

  const headers = [
    { name: "#", field: "idAnticipoCliente", sortable: false },
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "Cliente", field: "cliente", sortable: true },
    { name: "Forma Pago", field: "formaPago", sortable: false },
    { name: "Importe", field: "importe", sortable: false },
    { name: "Utilizado", field: "utilizado", sortable: false },
    { name: "Saldo", field: "saldo", sortable: false },
    { name: "Acciones", field: "acciones", sortable: false },
  ];

  /* totals for table footer */
  let tImp = 0, tUtil = 0, tSaldo = 0;

  return (
    <>
      <Header />
      <br /><br />
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">

              {/* ── page header ── */}
              <Row className="mb-2">
                <Col md={6}>
                  <Button size="sm" color="success" onClick={toggleCreate} id="botonListado">
                    + Nuevo Anticipo
                  </Button>
                  <Button size="sm" href="/ListadoAbonos" color="info" id="botonListado">
                    ← Abonos
                  </Button>
                </Col>
              </Row>

              <h3 className="text-center mb-3">Anticipos de Clientes</h3>

              {/* ── KPI cards ── */}
              <Row className="mb-4 g-3">
                {[
                  { label: "Anticipos activos", value: summaryStats.count, color: "#6366f1", icon: <IcFileInvoiceDollar /> },
                  { label: "Total recibido", value: fmt(summaryStats.totalImporte), color: "#0ea5e9", icon: <IcArrowDown /> },
                  { label: "Total utilizado", value: fmt(summaryStats.totalUtilizado), color: "#f59e0b", icon: <IcCheckCircle /> },
                  { label: "Saldo disponible", value: fmt(summaryStats.totalSaldo), color: "#10b981", icon: <IcWallet /> },
                ].map((k) => (
                  <Col md={3} key={k.label}>
                    <div style={{
                      background: "#fff",
                      borderRadius: 14,
                      padding: "16px 20px",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                      borderLeft: `4px solid ${k.color}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: "50%",
                        background: k.color + "18",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: k.color,
                      }}>
                        {k.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          {k.label}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                          {k.value}
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* ── filters ── */}
              <Row className="mb-3">
                <Col md={2}>
                  <Label>Fecha Inicio</Label>
                  <Input bsSize="sm" type="date" value={selectedFechaInicio}
                    onChange={(e) => { setSelectedFechaInicio(e.target.value); setCurrentPage(1); }} />
                </Col>
                <Col md={2}>
                  <Label>Fecha Fin</Label>
                  <Input bsSize="sm" type="date" value={selectedFechaFin}
                    onChange={(e) => { setSelectedFechaFin(e.target.value); setCurrentPage(1); }} />
                </Col>
                <Col md={3}>
                  <Label>Cliente</Label>
                  <Input
                    type="select"
                    value={selectedCliente}
                    onChange={(e) => { setSelectedCliente(e.target.value); setCurrentPage(1); }}
                    style={{ height: 31, fontSize: "0.875rem", padding: "0.25rem 0.5rem", color: "#0f172a" }}
                  >
                    <option value="">Todos</option>
                    {clientes
                      .sort((a, b) => a.nombre_comercial > b.nombre_comercial ? 1 : -1)
                      .map((c) => (
                        <option key={c._id} value={c._id}>{c.nombre_comercial}</option>
                      ))}
                  </Input>
                </Col>
              </Row>

              {/* ── pagination + search ── */}
              <Row className="mb-2">
                <Col md={6}>
                  <Pagination
                    total={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </Col>
                <Col md={6} className="d-flex flex-row-reverse">
                  <Search onSearch={(v) => { setSearch(v); setCurrentPage(1); }} />
                </Col>
              </Row>

              {/* ── table ── */}
              <Table size="sm" striped borderless className="table-responsive-xl">
                <TableHeader
                  headers={headers}
                  onSorting={(field, order) => setSorting({ field, order })}
                />
                <tbody>
                  {commentsData.map((c) => {
                    tImp += c.importe;
                    tUtil += c.utilizado;
                    tSaldo += c.saldo;
                    if (c.is_active === "Si") {
                      return (
                        <tr key={c._id}>
                          <td>{c.idAnticipoCliente}</td>
                          <td>{c.fecha}</td>
                          <td>{c.cliente}</td>
                          <td>
                            <Badge color={c.formaPago?.toLowerCase().includes("transfer") ? "info" : "secondary"} pill>
                              {c.formaPago || "—"}
                            </Badge>
                          </td>
                          <td className="text-right">{fmt(c.importe)}</td>
                          <td className="text-right">{fmt(c.utilizado)}</td>
                          <td className="text-right">
                            <span style={{
                              color: c.saldo > 0 ? "#10b981" : "#94a3b8",
                              fontWeight: c.saldo > 0 ? 700 : 400,
                            }}>
                              {fmt(c.saldo)}
                            </span>
                          </td>
                          <td>
                            {c.saldo > 0 && (
                              <Button size="sm" color="primary" title="Aplicar contra cargos"
                                onClick={() => abrirAplicar(c)} style={{ marginRight: 4 }}>
                                <IcDollar />
                              </Button>
                            )}
                            <Button size="sm" color="info" title="Editar anticipo"
                              onClick={() => abrirEditar(c)} style={{ marginRight: 4 }}>
                              <IcEdit />
                            </Button>
                            {c.utilizado === 0 && (
                              <Button size="sm" color="danger" title="Cancelar anticipo"
                                onClick={() => cancelarAnticipo(c._id, c.idAnticipoCliente)}>
                                <IcBan />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    } else {
                      return (
                        <tr key={c._id} style={{ backgroundColor: "#fecaca", opacity: 0.7 }}>
                          <td>{c.idAnticipoCliente}</td>
                          <td>{c.fecha}</td>
                          <td>{c.cliente}</td>
                          <td colSpan={4} className="text-center text-muted font-italic">CANCELADO</td>
                          <td />
                        </tr>
                      );
                    }
                  })}
                  {/* footer totals */}
                  <tr style={{ fontWeight: 700, borderTop: "2px solid #e2e8f0" }}>
                    <td colSpan={4} className="text-right">TOTAL</td>
                    <td className="text-right">{fmt(tImp)}</td>
                    <td className="text-right">{fmt(tUtil)}</td>
                    <td className="text-right">{fmt(tSaldo)}</td>
                    <td />
                  </tr>
                </tbody>
              </Table>

              <Row className="mt-2">
                <Col md={6}>
                  <Pagination
                    total={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          Modal: NUEVO ANTICIPO
      ════════════════════════════════════════ */}
      <Modal isOpen={modalCreate} toggle={toggleCreate}>
        <ModalHeader toggle={toggleCreate}>
          <h5 style={{ margin: 0 }}>Nuevo Anticipo de Cliente</h5>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Fecha</Label>
                <Input type="date" value={nFecha} onChange={(e) => setNFecha(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem" }} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Importe</Label>
                <Input type="number" min="0" step="any" placeholder="0.00" value={nImporte}
                  onChange={(e) => setNImporte(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem" }} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>Cliente</Label>
                <Input type="select" value={nCliente} onChange={(e) => setNCliente(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem", padding: "0.25rem 0.5rem", color: "#0f172a" }}>
                  <option value="">— Selecciona cliente —</option>
                  {clientes
                    .sort((a, b) => a.nombre_comercial > b.nombre_comercial ? 1 : -1)
                    .map((c) => (
                      <option key={c._id} value={c._id}>{c.nombre_comercial}</option>
                    ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Forma de Pago</Label>
                <Input type="select" value={nFormaPago} onChange={(e) => setNFormaPago(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem", padding: "0.25rem 0.5rem", color: "#0f172a" }}>
                  <option value="Transfer">Transfer</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="TDC">TDC</option>
                  <option value="Cheque">Cheque</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Observaciones</Label>
                <Input type="text" placeholder="Notas (opcional)" value={nObservaciones}
                  onChange={(e) => setNObservaciones(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem" }} />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={saveAnticipo} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Guardar"}
          </Button>
          <Button color="secondary" onClick={toggleCreate}>Cancelar</Button>
        </ModalFooter>
      </Modal>

      {/* ════════════════════════════════════════
          Modal: APLICAR ANTICIPO
      ════════════════════════════════════════ */}
      <Modal size="xl" isOpen={modalAplicar} toggle={toggleAplicar}>
        <ModalHeader toggle={toggleAplicar}>
          <h5 style={{ margin: 0 }}>
            Aplicar Anticipo #{apNumero} — {apClienteNombre}
          </h5>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
            Saldo disponible:{" "}
            <Badge color="success" pill style={{ fontSize: 13 }}>
              {fmt(apSaldo)}
            </Badge>
            {"  "}Total a aplicar:{" "}
            <Badge
              style={{
                fontSize: 13,
                backgroundColor: totalAplicar > apSaldo ? "#ef4444" : "#3b82f6",
              }}
              pill
            >
              {fmt(totalAplicar)}
            </Badge>
          </div>
        </ModalHeader>
        <ModalBody>
          {apLoading ? (
            <div className="text-center py-4">
              <Spinner style={{ width: 40, height: 40, color: "#6366f1" }} />
              <p className="mt-2 text-muted">Cargando cargos pendientes…</p>
            </div>
          ) : cargosDisponibles.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <IcCheckCircle size={48} style={{ marginBottom: 12, color: "#10b981" }} />
              <p>Este cliente no tiene cargos con saldo pendiente.</p>
            </div>
          ) : (
            <Table size="sm" striped borderless>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Descripción</th>
                  <th className="text-right">Cargo</th>
                  <th className="text-right">Saldo</th>
                  <th style={{ width: 130 }}>Aplicar</th>
                </tr>
              </thead>
              <tbody>
                {cargosDisponibles.map((c) => (
                  <tr key={c.id}>
                    <td>{c.idCargo}</td>
                    <td>{c.fecha}</td>
                    <td>{c.descripcion}</td>
                    <td className="text-right">{fmt(c.importe)}</td>
                    <td className="text-right">{fmt(c.saldo)}</td>
                    <td>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        bsSize="sm"
                        value={c.aplicar || ""}
                        placeholder="0.00"
                        onChange={(e) => handleCambioAplicar(c.id, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: "2px solid #e2e8f0" }}>
                  <td colSpan={5} className="text-right">Total aplicar</td>
                  <td>
                    <span style={{ color: totalAplicar > apSaldo ? "#ef4444" : "#10b981", fontWeight: 700 }}>
                      {fmt(totalAplicar)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          {cargosDisponibles.length > 0 && (
            <Button
              color="success"
              onClick={saveAplicar}
              disabled={apLoading || totalAplicar <= 0}
            >
              {apLoading ? <Spinner size="sm" /> : "Registrar aplicación"}
            </Button>
          )}
          <Button color="secondary" onClick={toggleAplicar}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* ════════════════════════════════════════
          Modal: EDITAR ANTICIPO
      ════════════════════════════════════════ */}
      <Modal isOpen={modalEdit} toggle={toggleEdit}>
        <ModalHeader toggle={toggleEdit}>
          <h5 style={{ margin: 0 }}>
            Editar Anticipo #{eNumero}
          </h5>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{eClienteNombre}</div>
        </ModalHeader>
        <ModalBody>
          {eUtilizado > 0 && (
            <div style={{
              background: "#fef9c3",
              border: "1px solid #fbbf24",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 12,
              fontSize: 13,
              color: "#92400e",
            }}>
              <IcExclamationTri style={{ marginRight: 6 }} />
              Ya se utilizaron <strong>{fmt(eUtilizado)}</strong>. El importe no puede quedar por debajo de ese valor.
            </div>
          )}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Importe</Label>
                <Input
                  type="number"
                  min={eUtilizado || 0}
                  step="any"
                  value={eImporte}
                  onChange={(e) => setEImporte(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem" }}
                />
                {parseFloat(eImporte) !== eImporteViejo && (
                  <small style={{ color: parseFloat(eImporte) > eImporteViejo ? "#10b981" : "#ef4444" }}>
                    {parseFloat(eImporte) > eImporteViejo ? "+" : ""}{fmt(parseFloat(eImporte) - eImporteViejo)} vs importe original
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Forma de Pago</Label>
                <Input
                  type="select"
                  value={eFormaPago}
                  onChange={(e) => setEFormaPago(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem", padding: "0.25rem 0.5rem", color: "#0f172a" }}
                >
                  <option value="Transfer">Transfer</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="TDC">TDC</option>
                  <option value="Cheque">Cheque</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>Observaciones</Label>
                <Input
                  type="text"
                  placeholder="Notas"
                  value={eObservaciones}
                  onChange={(e) => setEObservaciones(e.target.value)}
                  style={{ height: 31, fontSize: "0.875rem" }}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={saveEditar} disabled={eSaving}>
            {eSaving ? <Spinner size="sm" /> : "Guardar"}
          </Button>
          <Button color="secondary" onClick={toggleEdit}>Cancelar</Button>
        </ModalFooter>
      </Modal>

      {loader}
    </>
  );
}

export default ListadoAnticiposClientes;
